import { TabletButton } from '@campfire/tablet-button';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { LongTextTaskField } from '../../../../common/inputs/task-fields/TaskFields';
import { GET_ACTIVITY_APPLICATION_QUESTIONS_FOR_APPLY } from './applicant-application-questions.gql';
import {
  GetActivityApplicationQuestionsForApply,
  GetActivityApplicationQuestionsForApplyVariables,
} from './__generated__/GetActivityApplicationQuestionsForApply';

interface ApplicationQuestionsDialogProps {
  activityId: string | null;
  onClose: () => void;
  onConfirm: (answers: Array<{ questionId: string; answer: string }>) => void;
}

export const ApplicationQuestionsDialog = ({ activityId, onClose, onConfirm }: ApplicationQuestionsDialogProps) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const { data, loading } = useCampfireQuery<
    GetActivityApplicationQuestionsForApply,
    GetActivityApplicationQuestionsForApplyVariables
  >(GET_ACTIVITY_APPLICATION_QUESTIONS_FOR_APPLY, {
    options: { variables: { activityId: activityId ?? '' }, skip: !activityId },
  });

  const questions = useMemo(() => {
    if (!data) return [];
    const globals = data.vm.globalActivityApplicationQuestions ?? [];
    const activitySpecific = data.vm.activityApplicationQuestions ?? [];
    return [...globals, ...activitySpecific].sort((a, b) => a.order - b.order);
  }, [data]);

  // Restricted activities without any questions still apply — confirm immediately.
  useEffect(() => {
    if (activityId && !loading && data && questions.length === 0) {
      onConfirm([]);
    }
  }, [activityId, loading, data, questions.length]);

  useEffect(() => {
    if (!activityId) setAnswers({});
  }, [activityId]);

  const allRequiredAnswered = useMemo(() => {
    return questions
      .filter((question) => question.required)
      .every((question) => (answers[question.activityApplicationQuestionId] ?? '').trim() !== '');
  }, [questions, answers]);

  const handleConfirm = () => {
    const payload = questions
      .map((question) => ({
        questionId: question.activityApplicationQuestionId,
        answer: (answers[question.activityApplicationQuestionId] ?? '').trim(),
      }))
      .filter((entry) => entry.answer !== '');
    onConfirm(payload);
  };

  const open = !!activityId && questions.length > 0;

  return (
    <Dialog open={open} maxWidth='sm' fullWidth onClose={onClose}>
      <DialogTitle>
        <Typography variant='h6'>{'A few questions before you apply'}</Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          questions.map((question) => (
            <LongTextTaskField
              key={question.activityApplicationQuestionId}
              title={question.label}
              required={question.required}
              ariaId={question.activityApplicationQuestionId}
              ariaDescribedBy={`${question.activityApplicationQuestionId}-desc`}
              value={answers[question.activityApplicationQuestionId] ?? ''}
              onChange={(e) => {
                const { value } = e.target;
                setAnswers((prev) => ({ ...prev, [question.activityApplicationQuestionId]: value }));
              }}
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <TabletButton variant='text' onClick={onClose}>
          {'Cancel'}
        </TabletButton>
        <TabletButton variant='contained' color='primary' disabled={!allRequiredAnswered} onClick={handleConfirm}>
          {'Apply'}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
